import AlertMessage from '@/components/AlertMessage';
import Banner from '@/components/Banner';
import {
  CheckboxFieldMultiple,
  InputField,
  RadioField,
  SelectField,
  TextareaField,
} from '@/components/FormElements';
import PaymentMethod from '@/components/PaymentMethod';
import ShareDialog from '@/components/ShareDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { groupByCategory } from '@/lib/utils';
import { DynamicFieldProps, PageProps, Service } from '@/types';
import { router, usePage } from '@inertiajs/react';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import MeasurementDialog from './MeasurementForm';

const FormElements: Record<
  DynamicFieldProps['component'],
  | typeof InputField
  | typeof CheckboxFieldMultiple
  | typeof RadioField
  | typeof SelectField
  | typeof TextareaField
> = {
  input: InputField,
  checkbox: CheckboxFieldMultiple,
  radio: RadioField,
  select: SelectField,
  textarea: TextareaField,
};

type ServiceRequestData = {
  user_id: number;
  service_id: number;
  address: string;
  message: string;
  // other_service: string;
  amount: number;
  transaction_no: string;
} & Record<string, unknown>;

const MeasurementSizes = [
  '32',
  '34',
  '36',
  '38',
  '40',
  '42',
  '44',
  'other',
].map((size) => ({
  id: size,
  label: size,
}));

const ShowService = ({ service }: { service: Service }) => {
  const { user } = usePage<PageProps>().props.auth;

  const [openDialog, setOpenDialog] = useState(false);

  const form = useForm({
    defaultValues: async () =>
      await new Promise((resolve) => {
        const fieldData: ServiceRequestData = {
          user_id: user?.id ?? '',
          service_id: service.id,
          address: '',
          message: '',
          // other_service: '',
          amount: 0,
          transaction_no: '',
        };

        if (service.preferences && service.preferences.length > 0) {
          service.preferences.forEach((field) => {
            fieldData[field.name] = '';
          });
          fieldData.other_size = '';
        }
        resolve(fieldData);
      }),
  });

  // destructure form state to subscribe to proxy form state
  const { isSubmitting } = form.formState;

  useEffect(() => {
    window.dataLayer?.push({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            item_id: String(service.id),
            item_name: service.title,
          },
        ],
      },
    });
    // Fire once per page load, not on every re-render this component goes through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.id]);

  // const groupedServices = useMemo(
  //   () => groupByCategory(service.categories, ['group_type', 'category']),
  //   [service.categories]
  // );

  const [groupedServices, setGroupedServices] = useState(() =>
    groupByCategory(service.categories, ['group_type', 'category'])
  );

  const [selectedServices, setSelectedServices] = useState<
    Record<string, string | string[]>
  >({});

  // const [openDialog, setOpenDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [result, setResult] = useState<{
    status: 'success' | 'error';
    message: string;
  }>();

  const [measurementOptionField, standardSizeField] = form.watch([
    'measurement_options',
    'standard_size',
  ]) as [string, string];

  const measurementOptions = useRef(
    service.preferences?.find((field) => field.name === 'measurement_options')
      ?.options
  );

  const getMeasurementIndex = useMemo(
    () => measurementOptions.current?.indexOf(measurementOptionField),
    [measurementOptionField]
  );

  const resetState = () => {
    form.reset();
    setSelectedServices({});
  };

  const onBooking = (data: ServiceRequestData) => {
    if (!user) {
      router.visit(
        route('login', {
          redirect: window.location.pathname + window.location.search,
        })
      );
      return;
    }

    if (
      // data.other_service.trim() === '' &&
      service.categories.length > 0 &&
      Object.keys(selectedServices).length === 0
    ) {
      toast.error('Please select at least one service');
      return;
    }

    if (
      getMeasurementIndex === 1 &&
      (!data.measurements || Object.keys(data.measurements).length === 0)
    ) {
      toast.error('Please add measurements to proceed');
      return;
    }
    window.dataLayer?.push({
      event: 'begin_checkout',
      ecommerce: {
        items: [
          {
            item_id: String(service.id),
            item_name: service.title,
          },
        ],
      },
    });
    setOpenDialog(true);
  };

  const onValidData = async (data: ServiceRequestData) => {
    await new Promise<void>((resolve) => {
      const {
        user_id,
        service_id,
        address,
        message,
        // other_service,
        amount,
        transaction_no,
        ...prefData
      } = data;

      // If 'standard_size' exists and isn't set to 'other', reset 'other' to an empty string.
      if (prefData?.standard_size && prefData.standard_size !== 'other') {
        delete prefData.other_size;
      }

      const payload = {
        user_id,
        service_id,
        address,
        message,
        amount,
        transaction_no,
        details: JSON.stringify({
          services: { ...selectedServices /**, other_service */ },
          ...prefData,
        }),
      };

      router.post(route('services.service-request'), payload, {
        onError: (errors) => {
          setResult({
            status: 'error',
            message: errors[0] ?? 'Something went wrong. Retry!',
          });
        },
        onSuccess: ({ props }) => {
          if (props.status) {
            setResult({
              status: 'success',
              message: props.status as string,
            });
            resetState();
          }
        },
        onFinish: () => {
          setOpenDialog(false);
          setOpenAlert(true);
          resolve();
        },
      });
    });
  };

  return (
    <>
      <AlertMessage
        open={openAlert}
        onClose={(state) => {
          setOpenAlert(state);
          resetState();
        }}
        status={result?.status}
        message={result?.message}
      />

      <Drawer
        open={openDialog}
        onOpenChange={(state) => {
          form.clearErrors();
          setOpenDialog(state);
        }}
      >
        <DrawerContent className="sm:max-w-sm max-h-screen mx-auto">
          <DrawerHeader>
            <DrawerTitle>Payment Details</DrawerTitle>
            <DrawerDescription>
              Fill the below form to complete your payment.
            </DrawerDescription>
          </DrawerHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onValidData as () => Promise<void>)}
              className="flex flex-col gap-10 p-4 overflow-auto"
            >
              <div className="flex flex-col gap-4">
                <InputField
                  control={form.control}
                  name="amount"
                  label="Amount to be Paid"
                  startDecorator={
                    <span className="px-4 text-primary text-lg">₹</span>
                  }
                />

                <Typography.Subtle className="font-semibold">
                  Scan and Pay
                </Typography.Subtle>

                <PaymentMethod />
              </div>
              <div className="grid w-full max-w-sm items-center gap-6">
                {/* This form has no yup resolver — validation is per-field through
                    the `rules` prop InputField already forwards to react-hook-form.
                    Without this, blanks reached the database as '' and collided on
                    the column's unique index. */}
                <InputField
                  control={form.control}
                  name="transaction_no"
                  label="Transaction No."
                  rules={{ required: 'Transaction Number is required' }}
                />
              </div>
              <Button type="submit" disabled={isSubmitting}>
                Submit
              </Button>
            </form>
          </Form>
        </DrawerContent>
      </Drawer>

      <Banner
        image={service.image_url}
        alt={service.title}
        tagline={service.tagline}
        title={service.title}
        description={service.description}
      >
        {/* <Typography.Subtle className="mt-4 inline-flex items-center justify-center gap-1">
          {CalculateAvgRating(service.rating_count, service.review_count)}{' '}
          <StarFilledIcon className="size-4 text-primary" /> (
          {formatReviewCount(service.review_count)} reviews)
        </Typography.Subtle> */}
        <ShareDialog
          url={window.location.href}
          title={service.title + ' - ' + import.meta.env.VITE_APP_NAME}
          content={service.description}
        />
      </Banner>

      <Band tone="surface">
        <SectionLayout space="lg">
          <div className="grid grid-cols-1 md:grid-cols-2 auto-rows-auto gap-6">
            {service.categories.length > 0 && (
              <div className="flex flex-col gap-y-4">
                <Typography.Body className="font-semibold">
                  Choose from below provided services to get started.
                </Typography.Body>
                {Object.keys(groupedServices).map((groupedService) => {
                  const group = groupedServices[groupedService] as Record<
                    string,
                    Record<string, unknown>
                  >;
                  return (
                    <div
                      key={groupedService}
                      className="pt-6 flex flex-col gap-4"
                    >
                      {/* h2, not H3's default h3: the Banner above carries
                          this page's h1 and nothing else sits between it and
                          this section heading. The H3s further down this page
                          (Service Inquiry Form, Included Features, ...)
                          correctly follow it as-is. */}
                      <Typography.H3 as="h2">{groupedService}</Typography.H3>
                      {Object.keys(group).map((type) => {
                        const categoryData = group[type] as {
                          description: string;
                          sub_categories: string[];
                        };
                        const isMultiple = Boolean(service.multiple_select);
                        const category = `${groupedService}_${type}`;
                        return (
                          <div key={type} className="flex flex-col gap-2">
                            <Typography.Subtle className="underline underline-offset-4 decoration-1">
                              {type}
                            </Typography.Subtle>
                            {categoryData.description && (
                              <Typography.Subtle>
                                {categoryData.description}
                              </Typography.Subtle>
                            )}
                            <ToggleGroup
                              type={isMultiple ? 'multiple' : 'single'}
                              value={
                                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- `type` isn't a literal here, so TS can't correlate it with `value`'s branch
                                (selectedServices[category] as never) ??
                                (isMultiple ? [] : '')
                              }
                              onValueChange={(value: string | string[]) => {
                                setSelectedServices((prev) => {
                                  const newValue = { ...prev };
                                  if (value === '' || value.length === 0) {
                                    delete newValue[category];
                                  } else {
                                    newValue[category] = value;
                                  }
                                  return newValue;
                                });
                              }}
                              className="flex flex-wrap justify-start gap-2"
                              variant="outline"
                            >
                              {(categoryData.sub_categories ?? []).map(
                                (subCategory, index) => (
                                  <ToggleGroupItem
                                    key={index}
                                    value={subCategory}
                                    aria-label={`Toggle ${subCategory} from ${groupedService} ${type}`}
                                    className="h-auto text-primary data-[state=on]:bg-background data-[state=on]:border-primary focus:outline-hidden border-2 border-muted bg-popover p-2"
                                  >
                                    {subCategory}
                                  </ToggleGroupItem>
                                )
                              )}

                              <Input
                                id={`custom-category-${category}`}
                                name={`custom-category-${category}`}
                                placeholder='Type & Press "Enter" to add...'
                                className="w-max"
                                onKeyDown={(e) => {
                                  if (
                                    e.key === 'Enter' &&
                                    e.currentTarget.value !== ''
                                  ) {
                                    e.preventDefault();

                                    const value = e.currentTarget.value;

                                    setGroupedServices((prev) => {
                                      const newValue = { ...prev };
                                      (
                                        (
                                          newValue[groupedService] as Record<
                                            string,
                                            Record<string, unknown>
                                          >
                                        )[type] as {
                                          description: string;
                                          sub_categories: string[];
                                        }
                                      ).sub_categories.push(value);
                                      return newValue;
                                    });

                                    setSelectedServices((prev) => {
                                      const newValue = { ...prev };
                                      if (isMultiple) {
                                        newValue[category] = [
                                          ...((newValue[
                                            category
                                          ] as string[]) ?? []),
                                          value,
                                        ];
                                      } else {
                                        newValue[category] = value;
                                      }
                                      return newValue;
                                    });

                                    e.currentTarget.value = '';
                                  }
                                }}
                              />
                            </ToggleGroup>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex h-fit w-full max-w-lg flex-col gap-y-5 rounded-lg border bg-card p-6 shadow-pop">
              <Typography.H3>Service Inquiry Form</Typography.H3>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onBooking as () => Promise<void>)}
                  className="flex flex-col gap-6 space-y-4"
                >
                  {service.preferences?.map((field) => {
                    const { component, name, label, description, options } =
                      field;
                    const FormComponent: React.ElementType =
                      FormElements[component ?? 'input'];
                    return (
                      <FormComponent
                        key={field.name}
                        control={form.control}
                        name={name}
                        rules={{
                          required: field.required && 'Required',
                        }}
                        label={label}
                        description={
                          description ? () => description : undefined
                        }
                        placeholder={`${component === 'select' ? 'Select' : ''} ${label}`}
                        options={
                          options?.map((option) => ({
                            id: option,
                            label: option,
                          })) ?? []
                        }
                      />
                    );
                  })}

                  {getMeasurementIndex === 0 ? (
                    <div className="space-y-1">
                      <RadioField
                        control={form.control}
                        name="standard_size"
                        rules={{
                          required: 'Required',
                        }}
                        label="Standard Sizes"
                        options={MeasurementSizes}
                      />

                      <InputField
                        key="other_size"
                        control={form.control}
                        name="other_size"
                        rules={{
                          required: standardSizeField === 'other' && 'Required',
                        }}
                        label=""
                        placeholder="Other Size"
                        disabled={standardSizeField !== 'other'}
                      />
                    </div>
                  ) : getMeasurementIndex === 1 ? (
                    <MeasurementDialog
                      onResult={(result) =>
                        form.setValue('measurements', result)
                      }
                    />
                  ) : null}

                  <TextareaField
                    control={form.control}
                    name="address"
                    label="Address"
                    placeholder="Type your address here"
                    description={(val: string) =>
                      `${val?.trim().length} / 255 chars`
                    }
                    maxLength={255}
                    autoComplete="street-address"
                  />

                  <TextareaField
                    control={form.control}
                    name="message"
                    label="Message"
                    placeholder="Type your message here"
                    description={(val: string) =>
                      `${val?.trim().length} / 255 chars`
                    }
                    maxLength={255}
                    resizable
                  />

                  {/* <InputField
                    control={form.control}
                    name="other_service"
                    label="Other Services (if any)"
                    placeholder="Other Service"
                  /> */}

                  <div className="flex *:flex-1 gap-4">
                    <Button type="submit" disabled={isSubmitting}>
                      Book Service
                    </Button>
                    <ShareDialog
                      url={window.location.href}
                      title={service.title}
                      content={service.description}
                    />
                  </div>
                </form>
              </Form>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-6 auto-rows-auto">
            {service.included_features &&
              service.included_features !== '[]' && (
                <div className="flex flex-col gap-y-4">
                  <Typography.H3>Included Features</Typography.H3>
                  <div className="flex flex-wrap gap-2">
                    {(JSON.parse(service.included_features) as string[]).map(
                      (skill, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="py-1 text-sm font-medium"
                        >
                          {skill}
                        </Badge>
                      )
                    )}
                  </div>
                </div>
              )}

            {service.addon_features && service.addon_features !== '[]' && (
              <div className="flex flex-col gap-y-4">
                <Typography.H3>Addon Features</Typography.H3>
                <div className="flex flex-wrap gap-2">
                  {(JSON.parse(service.addon_features) as string[]).map(
                    (skill, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="py-1 text-sm font-medium"
                      >
                        {skill}
                      </Badge>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {service.highlights && service.highlights !== '[]' && (
            <div className="flex flex-col gap-y-4">
              <Typography.H3>Highlights</Typography.H3>
              <ul className="flex flex-col gap-y-4">
                {(JSON.parse(service.highlights) as string[]).map(
                  (highlight, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check size={20} className="mt-1 shrink-0 text-primary" />
                      <Typography.Body className="font-medium">
                        {highlight}
                      </Typography.Body>
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
        </SectionLayout>
      </Band>
    </>
  );
};

export default ShowService;
